/**
 * O CADASTRO DE UM ÓRGÃO OU ENTIDADE — o modal usado pela lista e pela ficha.
 *
 * Saiu de `prefeituras.tsx` na feature 009 sem mudar de comportamento: a ficha
 * (`organizacao-ficha.tsx`) precisa do MESMO formulário que a lista abre, e
 * duas cópias do fluxo de CNPJ, edição manual e credenciais divergiriam na
 * primeira correção feita só de um lado.
 *
 * Esteira do site institucional: cadastro entra como "pendente" e o operador
 * aprova (abre o modal pra conferir/corrigir e conclui com status ativo) ou
 * recusa (hard delete do cadastro + pessoas vinculadas).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,

  FormActions,
  FormGrid,
  NumberField,
  SelectField,
  TelefoneField,
  TextField,
  formatCnpj,
} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";

import {
  campoEditavel, campoEditavelNumero, oficiaisDiferem, readOnlyInputStyle, snapshotOficial,
} from "../../lib/edicao-manual";
import { CadastroPessoas } from "./cadastro-pessoas";
import { ConfirmarEdicaoManualModal } from "../../components/ConfirmarEdicaoManualModal";
import { AcessoMaster } from "../../components/AcessoMaster";
import { DocumentosCadastro } from "../../components/DocumentosCadastro";
import { DocumentosCarga } from "../../components/DocumentosCarga";
import type { AdminEstrategiaEncerramento, AdminPrefeitura, AdminPrefeituraInput } from "@atlas/sdk";
import type { ClassificacaoAdm, Esfera, Poder, TipoOrganizacao } from "@atlas/types";
import {
  CLASSIFICACAO_OPCOES,
  ESFERA_OPCOES,
  PODER_OPCOES,
  SUGESTAO_POR_TIPO,
  TIPO_ORGANIZACAO_OPCOES,
} from "./organizacao-campos";

// Os tres modos do modal — a intencao ja vem declarada pelo botao da tabela:
//   visualizar  olho:  tudo travado, sem salvar. So consulta.
//   editar      lapis: tudo destravado de cara, sem passo intermediario.
//   aprovar     esteira de pendentes: campos oficiais travados ate o operador
//               confirmar em "Editar manualmente" (quem preencheu foi outra
//               pessoa; sobrescrever a Receita segue sendo ato consciente).
export type ModoModal = "editar" | "aprovar" | "visualizar";

// formatCnpj + formatTelefone agora vem de @atlas/ui/web (compartilhado).
// readOnlyInputStyle + campoOficial vem de lib/edicao-manual (compartilhado
// com bancos.tsx, que tinha uma copia identica do estilo).

export function OrganizacaoModal({ initial, modo = "editar", todas, onClose }: { initial: AdminPrefeitura | null; modo?: ModoModal; todas: AdminPrefeitura[]; onClose: () => void }) {
  const aprovando = modo === "aprovar";
  const visualizando = modo === "visualizar";
  const qc = useQueryClient();
  const [form, setForm] = useState<AdminPrefeituraInput>({
    id: initial?.id,
    nome: initial?.nome ?? "",
    uf: initial?.uf ?? "SC",
    municipioIbge: initial?.municipioIbge ?? 0,
    prefeituraPaiId: initial?.prefeituraPaiId ?? null,
    tipoUnidade: initial?.tipoUnidade ?? "administracao_direta",
    tipo: initial?.tipo ?? "prefeitura",
    esfera: initial?.esfera ?? "municipal",
    poder: initial?.poder ?? "executivo",
    classificacaoAdm: initial?.classificacaoAdm ?? "direta",
    sigla: initial?.sigla ?? "",
    municipioNome: initial?.municipioNome ?? "",
    estrategiaEncerramento: initial?.estrategiaEncerramento ?? "omissao",
    // SEM `?? padrao`, ao contrario de quase todos os vizinhos daqui. O payload
    // e' `{...form}`, entao toda chave presente e' GRAVADA: inicializar com um
    // default faria todo save de todo orgao gravar corte 15, inclusive o de
    // quem nunca abriu esta secao. E' exatamente como o `tenantDeTeste` passou
    // a contar. `undefined` aqui significa "nao declarado", e a chave e'
    // removida do payload logo abaixo.
    corteRegra: initial?.corteRegra,
    tenantDeTeste: initial?.tenantDeTeste ?? false,
    distribuicaoAutomaticaDescontos: initial?.distribuicaoAutomaticaDescontos ?? false,
    modoIntegracao: initial?.modoIntegracao ?? "REST",
    status: initial?.status ?? "ativo",
    loginEmail: initial?.loginEmail ?? "",
    contatoEmail: initial?.contatoEmail ?? "",
    servidoresCount: initial?.servidoresCount ?? 0,
    folhaSincUrl: initial?.folhaSincUrl ?? "",
    permiteServidorEditarContato: initial?.permiteServidorEditarContato ?? false,
    // Organizacao nova nasce SEM exigir (pedido de 02/10/2026). Na edicao a
    // tela espelha a leitura do backend, em que so' `false` explicito dispensa:
    // um registro antigo sem a chave aparece marcado e nao e' desmarcado pelo
    // primeiro save.
    exigeSenhaAverbacao: initial ? initial.exigeSenhaAverbacao !== false : false,
    exclusividadesCartaoConsig: initial?.exclusividadesCartaoConsig ?? "",
    cnpj: initial?.cnpj ?? "",
    razaoSocial: initial?.razaoSocial ?? "",
    nomeFantasia: initial?.nomeFantasia ?? "",
    dataFundacao: initial?.dataFundacao ?? "",
    atividade: initial?.atividade ?? "",
    telefone: initial?.telefone ?? "",
    endereco: initial?.endereco ?? {},
  });
  const [error, setError] = useState<string | null>(null);
  const [cnpjInput, setCnpjInput] = useState<string>(initial?.cnpj ?? "");
  const [cnpjError, setCnpjError] = useState<string | null>(null);
  // Na aprovacao e na visualizacao ja abre expandido: o operador precisa VER
  // tudo, e esconder metade dos campos atras de um accordion contraria o
  // proposito da tela.
  const [avancado, setAvancado] = useState(aprovando || visualizando);
  // Aviso "Falta preencher" so aparece apos primeira tentativa de submit.
  // Antes: aparecia assim que o modal abria, poluindo a UI (cliente 21/07/2026).
  const [triedSubmit, setTriedSubmit] = useState(false);
  // So no modo aprovar: destrava os campos oficiais mediante confirmacao no
  // modal — ver lib/edicao-manual.ts.
  const [edicaoManual, setEdicaoManual] = useState(false);
  const [confirmandoEdicao, setConfirmandoEdicao] = useState(false);
  // Quem manda no travamento dos campos oficiais. Editar destrava direto: a
  // intencao veio do clique no lapis da tabela.
  const camposLiberados = modo === "editar" || (aprovando && edicaoManual);
  // Ultima origem OFICIAL conhecida dos campos da Receita: o registro carregado
  // ou a ultima consulta de CNPJ bem-sucedida. O save compara o form com isto
  // pra decidir a flag `dadosEditadosManualmente` — sem essa comparacao, com o
  // modo editar destravado, todo save marcaria o cadastro.
  const [baseOficial, setBaseOficial] = useState(() => snapshotOficial(initial));
  /**
   * O MUNICIPIO E' EXIGIDO SO' NA ESFERA MUNICIPAL (feature 009).
   *
   * A regra e' a mesma da API (`municipioObrigatorio`), e ela precisa existir
   * aqui tambem: a API ja aceita uma assembleia sem codigo IBGE, mas enquanto
   * o botao Salvar travar em `ibgeInvalido` para todo mundo, cadastrar um
   * orgao estadual pela TELA continua impossivel — e a mensagem diria para
   * digitar um codigo que ele nao tem.
   *
   * Fora da esfera municipal o campo e' opcional (a sede), mas se preenchido
   * ainda precisa ter os 7 digitos: codigo IBGE de municipio tem 2 da UF + 5
   * do municipio, e aceitar qualquer numero foi o que a checagem de formato
   * veio impedir quando o campo virou digitavel.
   */
  const municipioExigido = (form.esfera ?? "municipal") === "municipal";
  const ibgePreenchido = form.municipioIbge > 0;
  const ibgeFormatoOk = !ibgePreenchido || String(form.municipioIbge).length === 7;
  const ibgeInvalido = (municipioExigido && !ibgePreenchido) || !ibgeFormatoOk;
  // `ehFilial` SAIU NA FASE D (T060). Ele governava três coisas neste modal:
  // esconder as credenciais, travar o código IBGE e trocar o título. As três
  // vinham do regime antigo, em que o vínculo raiz→filial ERA a delegação de
  // acesso — sem login na filial, quem operava por ela era obrigatoriamente a
  // mãe. Desde 04/09/2026 (D-09) toda organização pode ter login próprio, e
  // desde T058 a API não apaga mais credencial de quem é apontado.
  //
  // Mantê-lo aqui deixaria a tela contradizendo a API no pior sentido: a
  // autarquia migrada continuaria SEM como receber a credencial que ela agora
  // tem direito de ter, porque a coluna diz o que ela era antes da migração.
  //
  // `form.prefeituraPaiId` continua no formulário e é reenviado como está: a
  // API aceita o valor que a linha já tem (é histórico) e recusa qualquer outro.
  //
  /**
   * CADASTRO NOVO PRECISA DE CREDENCIAL; O QUE JÁ EXISTE SEM ELA, NÃO.
   *
   * A organização migrada que era filial não tem login — quem operava por ela
   * era quem a atendia. Exigir a credencial ao editá-la travaria até uma troca
   * de status, e a exigência apareceria numa tela que o operador abriu para
   * outra coisa. O bloco de credenciais aparece para ela do mesmo jeito: dar
   * login é agora possível, e não obrigatório.
   */
  const exigeCredencial = !initial || !!initial.loginEmail;
  // Consulta CNPJ — BrasilAPI (Receita + Junta Comercial). Prefill do form.
  const consulta = useMutation({
    mutationFn: (cnpj: string) => atlas.admin.consultarCnpjPrefeitura(cnpj),
    onMutate: () => setCnpjError(null),
    onSuccess: (r) => {
      const d = r.dados;
      // Consulta nova sobrescreve tudo com dado oficial — volta a travar (so
      // tem efeito no modo aprovar; editar ja e' destravado por definicao).
      setEdicaoManual(false);
      const oficiais = {
        razaoSocial: (d.razao_social as string | undefined) ?? "",
        nomeFantasia: (d.nome_fantasia as string | undefined) ?? "",
        dataFundacao: (d.data_inicio_atividade as string | undefined) ?? "",
        atividade: (d.cnae_fiscal_descricao as string | undefined) ?? "",
        uf: ((d.uf as string | undefined) ?? form.uf).slice(0, 2).toUpperCase(),
        municipioIbge: (d.codigo_municipio_ibge as number | undefined) ?? form.municipioIbge,
        endereco: {
          logradouro: (d.logradouro as string | undefined) ?? "",
          numero: (d.numero as string | undefined) ?? "",
          complemento: (d.complemento as string | undefined) ?? "",
          bairro: (d.bairro as string | undefined) ?? "",
          cep: (d.cep as string | undefined) ?? "",
          municipio: (d.municipio as string | undefined) ?? "",
          uf: (d.uf as string | undefined) ?? "",
        },
      };
      // Esta consulta passa a ser a referencia oficial: alterar algo daqui pra
      // frente e' que caracteriza edicao manual.
      setBaseOficial(snapshotOficial(oficiais));
      setForm((prev) => ({
        ...prev,
        ...oficiais,
        cnpj: (d.cnpj as string | undefined) ?? cnpjInput.replace(/\D/g, ""),
        nome: (d.razao_social as string | undefined) ?? prev.nome,
        // BrasilAPI/ReceitaWS as vezes retorna 2 telefones concatenados
        // ("(11) 2475-8641 / (011) 6440-1929"). Pega so o primeiro pra caber
        // no campo e nao confundir o usuario (cliente 21/07/2026).
        telefone: ((d.ddd_telefone_1 as string | undefined) ?? "").split(/\s*\/\s*/)[0]?.trim() ?? "",
        contatoEmail: prev.contatoEmail || (d.email as string | undefined) || "",
      }));
    },
    onError: (err) => {
      // Backend retorna 400 com details.cnpj = mensagem detalhada; fallback
      // pra err.message se nao vier no shape esperado.
      const withDetails = err as { details?: { cnpj?: string }; message?: string; status?: number };
      const detalhe = withDetails.details?.cnpj;
      const msg = detalhe ?? withDetails.message ?? "Falha na consulta";
      if (withDetails.status === 404 || msg.includes("cnpj_nao_encontrado")) {
        setCnpjError("CNPJ não encontrado na Receita.");
      } else {
        setCnpjError(msg);
      }
    },
  });
  const buscarCnpj = () => {
    const digits = cnpjInput.replace(/\D/g, "");
    if (digits.length !== 14) {
      setCnpjError("CNPJ deve ter 14 dígitos.");
      return;
    }
    consulta.mutate(digits);
  };
  /**
   * Edita UM campo da regra de corte, materializando os outros a partir do
   * padrão na primeira vez que o operador toca na seção.
   *
   * O padrão precisa ser aplicado aqui, e não no estado inicial: um `?? 15` lá
   * em cima gravaria corte 15 em TODO save de TODO órgão, inclusive no de quem
   * nunca abriu esta seção. Aqui ele só entra quando alguém de fato declarou
   * alguma coisa — e aí declarar "dia 20" sem dizer a hora significa mesmo
   * "dia 20, no horário padrão", que é o que o operador espera.
   */
  const setCorte = (patch: Partial<NonNullable<AdminPrefeituraInput["corteRegra"]>>) =>
    setForm((f) => ({
      ...f,
      corteRegra: { dia: 15, hora: "17:00", seNaoUtil: "anterior", ...f.corteRegra, ...patch },
    }));
  const dadosPreenchidos = !!(form.razaoSocial || form.nomeFantasia || form.dataFundacao || form.atividade || form.endereco?.logradouro);
  const save = useMutation({
    mutationFn: () => {
      // Aprovar = mesmo upsert, com o status promovido pra ativo. Nao ha rota
      // dedicada; o backend ja aceita a transicao pendente -> ativo.
      const payload: AdminPrefeituraInput = {
        ...form,
        status: aprovando ? "ativo" : form.status,
        // Marca so quando um campo oficial de fato saiu do valor que veio da
        // Receita (ou do registro carregado). Abrir e salvar sem mexer nao
        // marca. Uma vez marcado, continua marcado — o dado sobrescrito na mao
        // nao "volta a ser oficial". A API carimba a data e audita.
        dadosEditadosManualmente:
          initial?.dadosEditadosManualmente
          || oficiaisDiferem(snapshotOficial(form), baseOficial)
          || undefined,
      };
      if (!payload.loginEmail) delete payload.loginEmail;
      if (!payload.contatoEmail) delete payload.contatoEmail;
      if (!payload.folhaSincUrl) delete payload.folhaSincUrl;
      if (!payload.cnpj) delete payload.cnpj;
      // Regra nao declarada nao vai no corpo: a API preserva o que esta gravado
      // quando a chave falta, e mandar `undefined` explicito seria a diferenca
      // entre "nao mexi" e "apague".
      if (!payload.corteRegra) delete payload.corteRegra;
      return atlas.admin.upsertPrefeitura(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "prefeituras"] });
      // Aprovar tira o cadastro da fila; salvar uma prefeitura ja ativa nao muda
      // a contagem, mas invalidar sempre e' mais barato que raciocinar sobre isso.
      qc.invalidateQueries({ queryKey: ["admin", "cadastros-pendentes"] });
      onClose();
    },
    onError: (err) => {
      // Backend retorna 400 com details.fieldErrors[campo] = mensagem. Expande
      // pra mostrar CAMPO+MOTIVO ao inves de so "Dados invalidos" generico.
      const e = err as { message?: string; details?: Record<string, unknown> };
      const det = e.details as { fieldErrors?: Record<string, string[]>; formErrors?: string[] } | undefined;
      const campos: string[] = [];
      if (det?.fieldErrors) {
        for (const [k, msgs] of Object.entries(det.fieldErrors)) {
          if (msgs?.length) campos.push(`${k}: ${msgs.join(", ")}`);
        }
      }
      // Erros no nivel raiz (ex.: loginEmail ja em uso vem em details.loginEmail direto)
      if (det && !det.fieldErrors) {
        for (const [k, v] of Object.entries(det)) {
          if (typeof v === "string") campos.push(`${k}: ${v}`);
        }
      }
      if (det?.formErrors?.length) campos.push(...det.formErrors);
      const base = e.message || "Erro ao salvar";
      setError(campos.length > 0 ? `${base} — ${campos.join(" | ")}` : base);
    },
  });

  const loginTrocado = !!initial?.loginEmail
    && (form.loginEmail ?? "").trim().toLowerCase() !== initial.loginEmail;

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={modalBackdrop}
    >
      <div onMouseDown={(e) => e.stopPropagation()} style={modalCard}>
        <h3 style={{ margin: 0 }}>
          {aprovando
            ? `Aprovar cadastro — ${initial?.nome ?? ""}${initial?.uf ? `/${initial.uf}` : ""}`
            : visualizando
              ? `Visualizar ${initial?.nome ?? ""}${initial?.uf ? `/${initial.uf}` : ""}`
              : initial ? `Editar ${initial.nome}/${initial.uf}` : "Novo órgão ou entidade"}
        </h3>

        {/* Banner do modo aprovacao: o operador esta CONFERINDO um cadastro que
            veio do site, nao editando um registro ja ativo. Deixa explicito o
            efeito do botao final (libera o acesso da prefeitura). */}
        {aprovando ? (
          <div style={{
            padding: 14, borderRadius: 10,
            background: "color-mix(in srgb, var(--emerald-500) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--emerald-500) 30%, transparent)",
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--emerald-500)", textTransform: "uppercase", marginBottom: 6 }}>
              Cadastro pendente do site institucional
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Confira e corrija os dados abaixo — inclusive representantes legais, responsáveis
              e usuário master. Ao confirmar, o cadastro passa para <b style={{ color: "var(--text)" }}>ATIVO</b> e
              o master ativa o acesso em “Primeiro acesso”, com o e-mail de login e o código enviado a ele.
            </div>
          </div>
        ) : null}

        {/* O PASSO 0 — VÍNCULO SAIU NA FASE D (T060).
            Ele perguntava "este cadastro é um órgão, ou uma unidade pagadora de
            outro?" e gravava `prefeituraPaiId`. Era um avanço sobre o que havia
            antes (o parentesco existia só como coincidência de código IBGE
            digitado à mão), mas dizia UMA coisa só, sem papel, sem vigência e
            sem autor — e a mesma pergunta feita no cadastro obrigava a saber a
            resposta no minuto em que a organização nasce.
            Agora o vínculo é RELAÇÃO INSTITUCIONAL, criada na ficha da
            organização ou na tela de relações: com papel, competência inicial,
            justificativa e quem decidiu. A API recusa `prefeituraPaiId` novo
            desde T058. */}

        {/* PASSO 1 — Consulta CNPJ. Prefill de todos os campos oficiais via
            BrasilAPI (base publica que agrega Receita Federal + Junta Comercial).
            Aparece TAMBEM em modo edicao: os campos oficiais sao readOnly, entao
            sem este passo um cadastro antigo (criado antes da consulta CNPJ, ou
            sem CNPJ) ficava com razao social/endereco vazios pra sempre — nao
            havia caminho pela interface pra preencher. Rebuscar aqui tambem
            atualiza dados oficiais que mudaram na Receita. */}
        <div>
            <label style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
              CNPJ do órgão ou entidade
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={cnpjInput}
                onChange={(e) => setCnpjInput(formatCnpj(e.target.value))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buscarCnpj(); } }}
                placeholder="00.000.000/0000-00"
                maxLength={18}
                readOnly={visualizando}
                style={{
                  flex: 1, padding: "10px 12px", borderRadius: 8,
                  border: "1px solid var(--border-strong)",
                  background: "var(--bg-elev)", color: "var(--text)",
                  fontSize: 14, fontFamily: "var(--font-mono)",
                  ...(visualizando ? readOnlyInputStyle : {}),
                }}
              />
              {/* Pesquisar sobrescreve o form inteiro — nao existe em visualizar. */}
              {!visualizando ? (
                <Button type="button" onClick={buscarCnpj} disabled={consulta.isPending}>
                  {consulta.isPending ? "Buscando…" : "Pesquisar"}
                </Button>
              ) : null}
            </div>
            {cnpjError ? <div style={{ color: "var(--danger-500)", fontSize: 12, marginTop: 6 }}>{cnpjError}</div> : null}
            {!visualizando && !cnpjError && !dadosPreenchidos ? (
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                Consulta pública (Receita + Junta Comercial). Preenche razão social, endereço, atividade e código IBGE automaticamente.
              </div>
            ) : null}
            {!visualizando && !cnpjError && dadosPreenchidos && initial ? (
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                Pesquise novamente para reatualizar os dados oficiais pela Receita.
              </div>
            ) : null}
        </div>

        {/* PASSO 2 — Dados oficiais. Aparece:
            - No cadastro: apos consulta CNPJ bem-sucedida (dadosPreenchidos=true)
            - Em edicao: SEMPRE (mesmo se vazios — cadastros antigos nao tinham
              o campo salvo; permite pelo menos editar telefone) */}
        {(dadosPreenchidos || initial) ? (
          <div style={{ padding: 14, background: "var(--bg-elev-2)", borderRadius: 10, border: "1px solid var(--border)" }}>
            {/* O rotulo segue o estado REAL dos campos. Antes dizia sempre
                "preenchidos" em modo edicao, mesmo com tudo vazio — o que
                escondia justamente o cadastro que precisava da busca.
                Terceiro estado: edicao manual habilitada (dourado, so no modo
                aprovar — nos outros o travamento vem do botao da tabela). */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: edicaoManual ? "var(--gold-500)" : dadosPreenchidos ? "var(--emerald-500)" : "var(--warn-500)", textTransform: "uppercase" }}>
                {edicaoManual
                  ? "✎ Edição manual habilitada"
                  : dadosPreenchidos ? "✓ Dados oficiais preenchidos" : "⚠ Dados oficiais não preenchidos"}
              </div>
              {/* Valvula de escape SO na esteira de aprovacao: ali o cadastro
                  veio do site (outra pessoa preencheu) e os campos nascem
                  travados. Em editar nao existe — a intencao ja foi declarada
                  no lapis da tabela. */}
              {aprovando && !edicaoManual ? (
                <Button variant="ghost" type="button" onClick={() => setConfirmandoEdicao(true)} style={{ flexShrink: 0, fontSize: 12, padding: "6px 10px" }}>
                  ✎ Editar manualmente
                </Button>
              ) : null}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
              {!dadosPreenchidos
                ? visualizando
                  ? "Este cadastro não tem os dados oficiais da Receita/Junta Comercial."
                  : "Este cadastro não tem os dados oficiais. Informe o CNPJ no campo acima e clique em Pesquisar para preencher."
                : edicaoManual
                  ? "Edição manual ativa — os dados abaixo podem ser alterados e deixam de refletir exatamente a Receita/Junta Comercial. Pesquisar o CNPJ novamente restaura os dados oficiais e volta a travar os campos."
                  : camposLiberados
                    ? "Provenientes da Receita/Junta Comercial. Alterar qualquer campo aqui marca o cadastro como editado manualmente."
                    : visualizando
                      ? "Somente leitura — provenientes da Receita/Junta Comercial."
                      : "Somente leitura — provenientes da Receita/Junta Comercial. Apenas o telefone pode ser editado."}
            </div>
            {/* Registro que ja foi editado manualmente em outra sessao — o
                operador precisa saber que o dado abaixo pode nao ser da Receita. */}
            {!edicaoManual && initial?.dadosEditadosManualmente ? (
              <div style={{ fontSize: 11, color: "var(--gold-500)", marginBottom: 10 }}>
                ✎ Este cadastro já teve dados editados manualmente
                {initial.dadosEditadosManualmenteEm ? ` em ${new Date(initial.dadosEditadosManualmenteEm).toLocaleString("pt-BR")}` : ""}.
              </div>
            ) : null}
            <FormGrid cols={2}>
              {/* `nome` acompanha a razao social — e' o campo exibido na tabela
                  e exigido na validacao (a consulta de CNPJ faz o mesmo). Sem
                  isso, corrigir a razao social na mao deixava a lista mostrando
                  o valor antigo. */}
              <TextField label="Razão social" {...campoEditavel(camposLiberados, form.razaoSocial ?? "", (v) => setForm({ ...form, razaoSocial: v, nome: v }))} />
              <TextField label="Nome fantasia" {...campoEditavel(camposLiberados, form.nomeFantasia ?? "", (v) => setForm({ ...form, nomeFantasia: v }))} />
              <TextField label="Data de fundação" {...campoEditavel(camposLiberados, form.dataFundacao ?? "", (v) => setForm({ ...form, dataFundacao: v }))} hint={camposLiberados ? "Formato AAAA-MM-DD." : undefined} />
              <TextField label="Atividade principal" {...campoEditavel(camposLiberados, form.atividade ?? "", (v) => setForm({ ...form, atividade: v }))} />
              <TextField label="UF" {...campoEditavel(camposLiberados, form.uf, (v) => setForm({ ...form, uf: v.slice(0, 2).toUpperCase() }))} required maxLength={2} />
              {/* PRECISA ser digitavel: a busca por CNPJ preenche este campo
                  cruzando o nome do municipio da Receita com a base do IBGE.
                  Quando o cruzamento falha (nome grafado diferente, municipio
                  novo, distrito), o campo ficava vazio, o formulario exigia
                  "Codigo IBGE e obrigatorio" e nao havia como digitar: a unica
                  saida sugerida era "refaca a busca por CNPJ", que ia falhar de
                  novo pelo mesmo motivo. Cadastro impossivel de concluir.
                  Cliente 12/08/2026, item 4 da lista. No modo editar ja abre
                  liberado; no aprovar, pelo botao "Editar manualmente". */}
              {/* Filial nao digita o municipio: ele vem da mae. Digitar de novo
                  e' exatamente como as unidades do mesmo municipio passavam a
                  divergir e deixavam de se agrupar. */}
              <NumberField
                label="Código IBGE"
                {...campoEditavelNumero(camposLiberados, form.municipioIbge, (v) => setForm({ ...form, municipioIbge: v }))}
                required={municipioExigido}
                hint={!municipioExigido
                    // Fora da esfera municipal o municipio e' so' a SEDE. Dizer
                    // isso no campo evita o operador procurar um codigo IBGE
                    // para uma assembleia, que nao pertence a municipio nenhum.
                    ? "Opcional (sede) — um órgão estadual ou federal não pertence a um município."
                    : camposLiberados
                      ? (form.municipioIbge > 0
                          ? "Preenchido pela busca do CNPJ. Corrija se o município estiver errado."
                          : "A busca do CNPJ não encontrou o município. Digite os 7 dígitos do código IBGE.")
                      : form.municipioIbge > 0
                        ? undefined
                        : visualizando
                          ? "⚠ Não preenchido"
                          : "⚠ Não preenchido — refaça a busca por CNPJ ou use “Editar manualmente”"}
                error={ibgeInvalido
                  ? (municipioExigido && !ibgePreenchido
                      ? "Município é obrigatório na esfera municipal."
                      : "Código IBGE tem 7 dígitos (ex.: 3550308 = São Paulo/SP).")
                  : undefined}
              />
              {/* Telefone fica FORA do campoEditavel: no modo aprovar continua
                  editavel mesmo com os oficiais travados (o telefone da Receita
                  costuma estar desatualizado). So visualizar trava. */}
              <TelefoneField
                label="Telefone"
                value={form.telefone ?? ""}
                onChange={visualizando ? () => undefined : (e) => setForm({ ...form, telefone: e.target.value })}
                readOnly={visualizando}
                style={visualizando ? readOnlyInputStyle : undefined}
                required
                hint={visualizando ? undefined : "Obrigatório — editável se o telefone oficial estiver desatualizado"}
              />
              <TextField label="CEP" {...campoEditavel(camposLiberados, form.endereco?.cep ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, cep: v } }))} />
              <TextField label="Logradouro" {...campoEditavel(camposLiberados, form.endereco?.logradouro ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, logradouro: v } }))} />
              <TextField label="Número" {...campoEditavel(camposLiberados, form.endereco?.numero ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, numero: v } }))} />
              <TextField label="Bairro" {...campoEditavel(camposLiberados, form.endereco?.bairro ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, bairro: v } }))} />
              <TextField label="Município" {...campoEditavel(camposLiberados, form.endereco?.municipio ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, municipio: v } }))} />
              {/* Complemento ja existia no payload (endereco.complemento) e na
                  coluna, mas nao tinha campo na tela — o dado da Receita
                  chegava e ficava invisivel. */}
              <TextField label="Complemento" {...campoEditavel(camposLiberados, form.endereco?.complemento ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, complemento: v } }))} />
            </FormGrid>
            {/* Protocolo do cadastro feito pelo site institucional. So aparece
                quando existe — cadastro criado aqui pela averbadora nao tem. */}
            {initial?.protocoloCadastro ? (
              <div style={{ marginTop: 10 }}>
                <FormGrid cols={2}>
                  <TextField label="Protocolo do cadastro" value={initial.protocoloCadastro} readOnly style={readOnlyInputStyle} onChange={() => undefined} hint="Gerado pelo cadastro no site institucional." />
                </FormGrid>
              </div>
            ) : null}
            {/* Pessoas do cadastro (tabelas da migration 0001). Salvam direto na
                API, fora do "Salvar" do modal — ver cadastro-pessoas.tsx. */}
            <CadastroPessoas kind="prefeituras" tenantId={initial?.id} somenteLeitura={visualizando} />
          </div>
        ) : null}

        {/* UNIDADE PAGADORA — qual folha este registro é.
            Um município não processa uma folha só. Morada Nova tem três — a
            Administração Direta, a A.M.T e o I.M.A.M.N. — e cada uma numera
            matrícula e rubrica por conta própria: o Banco do Brasil é a verba
            285, 248 e 234 nelas.
            Cada folha já é um registro aqui, e é por isso que o catálogo de
            rubricas acerta ao escopar por prefeitura. O que faltava era dizer
            isso: sem o tipo, três registros com nomes diferentes parecem três
            municípios em vez de três pagadoras do mesmo. */}
        <div style={{ padding: 14, background: "var(--bg-elev-2)", borderRadius: 10, border: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", marginBottom: 4 }}>
            Unidade pagadora
          </div>
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 10px" }}>
            Um município com mais de uma folha tem um cadastro por folha. Elas se agrupam pelo código IBGE.
          </p>
          <FormGrid cols={2}>
            {/* CLASSIFICAÇÃO INSTITUCIONAL (feature 009).
                Quatro campos independentes: o tipo diz o que a organização é, e
                escolher um tipo preenche os outros três pela sugestão — que o
                operador pode corrigir. "Administração direta" saiu daqui e virou
                valor da classificação, porque ela não é sinônimo de prefeitura:
                uma secretaria estadual também é direta. */}
            <SelectField
              label="Tipo de órgão ou entidade"
              value={form.tipo ?? "prefeitura"}
              onChange={(e) => {
                const tipo = e.target.value as TipoOrganizacao;
                const s = SUGESTAO_POR_TIPO[tipo];
                setForm({
                  ...form,
                  tipo,
                  // A sugestão sobrescreve, porque trocar o tipo é justamente
                  // dizer "isto é outra coisa". Quem quiser divergir corrige nos
                  // campos ao lado, que estão logo abaixo.
                  esfera: s.esfera ?? form.esfera ?? "municipal",
                  poder: s.poder,
                  classificacaoAdm: s.classificacaoAdm,
                });
              }}
              options={TIPO_ORGANIZACAO_OPCOES}
              // `<select>` ignora readOnly — no modo visualizar precisa de disabled.
              disabled={visualizando}
              hint="O que esta organização é. Preenche esfera, poder e classificação; todos editáveis."
            />
            <SelectField
              label="Esfera"
              value={form.esfera ?? "municipal"}
              onChange={(e) => setForm({ ...form, esfera: e.target.value as Esfera })}
              options={ESFERA_OPCOES}
              disabled={visualizando}
              hint={
                (form.esfera ?? "municipal") === "municipal"
                  ? "Município é obrigatório nesta esfera."
                  : "Município vira a sede, e é opcional."
              }
            />
            <SelectField
              label="Poder"
              value={form.poder ?? "executivo"}
              onChange={(e) => setForm({ ...form, poder: e.target.value as Poder })}
              options={PODER_OPCOES}
              disabled={visualizando}
            />
            <SelectField
              label="Classificação administrativa"
              value={form.classificacaoAdm ?? "direta"}
              onChange={(e) => setForm({ ...form, classificacaoAdm: e.target.value as ClassificacaoAdm })}
              options={CLASSIFICACAO_OPCOES}
              disabled={visualizando}
              hint="Independente do tipo: uma secretaria estadual é direta, uma autarquia é indireta."
            />
            <TextField
              label="Sigla"
              {...campoEditavel(!visualizando, form.sigla ?? "", (v) => setForm({ ...form, sigla: v }))}
              placeholder="A.M.T"
              hint="Como a casa se chama. Fora do tipo Prefeitura, é o que o servidor vê."
            />
            <TextField
              label="Município"
              {...campoEditavel(!visualizando, form.municipioNome ?? "", (v) => setForm({ ...form, municipioNome: v }))}
              placeholder="Morada Nova"
              hint="Nome do município a que esta folha pertence — é o rótulo do agrupamento."
            />
            <SelectField
              label="Como anunciar desconto encerrado"
              value={form.estrategiaEncerramento ?? "omissao"}
              onChange={(e) => setForm({ ...form, estrategiaEncerramento: e.target.value as AdminEstrategiaEncerramento })}
              options={[
                { value: "omissao", label: "Por omissão — a linha sai do arquivo" },
                { value: "codigo", label: "Código E — linha explícita de exclusão" },
              ]}
              disabled={visualizando}
              hint={form.estrategiaEncerramento === "codigo"
                ? "⚠ Confirme com o órgão antes: o código E é padrão da Atlas e nunca apareceu num arquivo real. Se o ERP dele não o reconhecer, a linha pode ser rejeitada."
                : "O padrão. Serve para o ERP que lê a posição inteira do mês e substitui o que tinha."}
            />
          </FormGrid>

          {/* CORTE E REPASSE — a fonte única das datas da folha (ADR-0012).
              Até 15/09/2026 estes dois dias eram editados no formulário do
              CONVÊNIO, um por banco, e a folha usava o menor entre eles. A
              folha é uma só por órgão: a data é propriedade dele.

              Só aparece para quem processa folha própria, pela mesma razão que
              a aba Folha da ficha se cala nesse caso — configurar o corte de
              um órgão cujos servidores são descontados na folha de outro é
              oferecer um controle que não chega a lugar nenhum. */}
          {initial?.temFolhaPropria !== false ? (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ margin: "0 0 4px", fontSize: 13, color: "var(--text)" }}>Corte e repasse da folha</h4>
              <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--text-dim)" }}>
                Vale para todos os convênios deste órgão. Em branco, a Atlas usa o padrão:{" "}
                <b>dia 15, 17:00, antecipando quando cai em fim de semana</b>. A exceção de um mês específico
                (feriado municipal, determinação dos gestores) continua sendo feita na folha daquela competência.
              </p>
              <FormGrid>
                <NumberField
                  label="Dia de corte"
                  value={form.corteRegra?.dia ?? ""}
                  min={1}
                  max={31}
                  disabled={visualizando}
                  onChange={(e) => setCorte({ dia: Number(e.target.value) })}
                  hint="Até este dia uma averbação ainda entra no desconto do mês."
                />
                <TextField
                  label="Hora do corte"
                  value={form.corteRegra?.hora ?? ""}
                  placeholder="17:00"
                  disabled={visualizando}
                  onChange={(e) => setCorte({ hora: e.target.value })}
                  hint="HH:MM, horário de Brasília. Às 16:59 ainda entra averbação."
                />
                <SelectField
                  label="Se o corte cair em fim de semana"
                  value={form.corteRegra?.seNaoUtil ?? "anterior"}
                  disabled={visualizando}
                  onChange={(e) => setCorte({ seNaoUtil: e.target.value as "anterior" | "seguinte" })}
                  options={[
                    { value: "anterior", label: "Antecipa — corta na sexta" },
                    { value: "seguinte", label: "Adia — corta na segunda" },
                  ]}
                  hint="Antecipar protege: nunca faz uma averbação entrar num movimento já emitido. Feriado não é calculado — use a exceção da folha."
                />
                <NumberField
                  label="Dia de repasse"
                  value={form.corteRegra?.diaRepasse ?? ""}
                  min={1}
                  max={31}
                  disabled={visualizando}
                  onChange={(e) =>
                    setCorte({ diaRepasse: e.target.value === "" ? undefined : Number(e.target.value) })
                  }
                  hint="No mês seguinte à competência. Em branco, cai no próprio dia do corte."
                />
              </FormGrid>
            </div>
          ) : null}
          {/* Fica FORA do `avancado`, ao lado dos campos institucionais: a marca
              muda o que os números da plataforma dizem, e esconder isso atrás de
              um accordion é como o tenant de teste passou a somar sem ninguém
              perceber. Item 9 do PENDENCIAS.md. */}
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "var(--text-muted)", cursor: visualizando ? "default" : "pointer", marginTop: 12 }}>
            <input
              type="checkbox"
              checked={form.tenantDeTeste ?? false}
              disabled={visualizando}
              onChange={(e) => setForm({ ...form, tenantDeTeste: e.target.checked })}
              style={{ marginTop: 3 }}
            />
            <span>
              <b style={{ color: "var(--text)" }}>Organização de teste — não conta nos números da plataforma</b>
              <br />
              Para o cadastro que a equipe usa para experimentar. Sai das contagens e do painel sempre, e da lista de
              organizações por padrão — o filtro “Mostrar organizações de teste” traz de volta. <b>Não afeta o
              acesso</b>: quem tem login nela continua entrando normalmente.
            </span>
          </label>
        </div>

        {/* Documentos anexados no cadastro do site. Aparece em visualizar E em
            aprovar: conferir o documento e' justamente o que o operador faz
            antes de liberar o acesso. Fora do modo editar (nada aqui e'
            editavel) e sem `initial` nao existe id pra buscar os bytes. */}
        {initial && (visualizando || aprovando) ? (
          <DocumentosCadastro
            docFrenteKey={initial.docFrenteKey}
            docVersoKey={initial.docVersoKey}
            selfieKey={initial.selfieKey}
            veioDoSite={!!initial.protocoloCadastro}
            carregar={(parte) => atlas.admin.prefeituraDocumentoBlob(initial.id, parte)}
          />
        ) : null}

        {/* Anexos do Passo 6 (arquivos do convênio). Bloco proprio, logo abaixo
            dos documentos de identidade e sob as MESMAS condicoes: aparece em
            visualizar e em aprovar, porque conferir o que o orgao mandou faz
            parte de liberar o acesso. A lista vem do banco (tabela propria), por
            isso o componente busca sozinho em vez de receber por prop. */}
        {initial && (visualizando || aprovando) ? (
          <DocumentosCarga prefeituraId={initial.id} veioDoSite={!!initial.protocoloCadastro} />
        ) : null}

        {/* PASSO 3 — Credenciais de acesso da organização. So o e-mail de
            login: a senha nasce no primeiro acesso do master (09/10/2026).

            TODA ORGANIZAÇÃO TEM LOGIN PRÓPRIO. Até 04/09/2026 este bloco era
            substituído, na filial, por um aviso de "sem login próprio": quem
            operava pela autarquia era o login da mãe. A regra caiu com D-09 — a
            organização atendida continua dona de si, inclusive do direito de
            entrar no próprio portal — e quem delega passou a ser a relação, com
            papel e vigência. Esconder o bloco aqui deixaria a autarquia migrada
            sem como receber a credencial que ela agora pode ter. */}
        <div style={{ padding: 14, background: "color-mix(in srgb, var(--accent) 6%, transparent)", borderRadius: 10, border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--border))" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--accent)", textTransform: "uppercase", marginBottom: 10 }}>
            🔑 Credenciais de acesso da organização
          </div>
          <FormGrid cols={2}>
            <TextField
              label="E-mail de login"
              {...campoEditavel(!visualizando, form.loginEmail ?? "", (v) => setForm({ ...form, loginEmail: v }))}
              type="email"
              placeholder="rh@orgao.gov.br"
              required
              hint="O master ativa o acesso em “Primeiro acesso” com este e-mail e depois o usa em todo login."
              // Evita Chrome autofill (com CPF do login) — nome unico +
              // autoComplete off (cliente 21/07/2026: nao vazar credencial anterior).
              autoComplete="off"
              name="atlas-nova-prefeitura-login-email"
            />
            <AcessoMaster hasPassword={!!initial?.hasPassword} loginTrocado={loginTrocado} />
          </FormGrid>
        </div>

        {/* PASSO 4 — Opções avançadas (colapsadas). Preservam funcionalidade
            legada de integracao/folha/exclusividades sem poluir o novo fluxo. */}
        <details open={avancado} onToggle={(e) => setAvancado((e.currentTarget as HTMLDetailsElement).open)}>
          <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", userSelect: "none" }}>
            Opções avançadas
          </summary>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
            <FormGrid cols={2}>
              {/* `<select>` ignora readOnly — travar exige `disabled` (mesmo
                  padrao de servidores/_editModal.tsx). Vale pros checkboxes
                  logo abaixo tambem. */}
              <SelectField
                label="Modo de integração"
                value={form.modoIntegracao}
                disabled={visualizando}
                onChange={(e) => setForm({ ...form, modoIntegracao: e.target.value as AdminPrefeituraInput["modoIntegracao"] })}
                options={[
                  { value: "REST", label: "REST" },
                  { value: "SOAP", label: "SOAP" },
                  { value: "CSV", label: "CSV" },
                  { value: "MANUAL", label: "MANUAL" },
                ]}
              />
              {/* No modo aprovar o status e' decidido pelo botao do rodape —
                  deixar o select editavel aqui so criaria contradicao (escolher
                  "Pausado" e mesmo assim o save gravar "ativo"). */}
              {aprovando ? (
                <TextField label="Situação" value="Pendente → Ativo ao confirmar" readOnly style={readOnlyInputStyle} onChange={() => undefined} hint="Definida pelo botão “Confirmar e aprovar cadastro”." />
              ) : (
                <SelectField
                  label="Situação"
                  value={form.status}
                  disabled={visualizando}
                  onChange={(e) => setForm({ ...form, status: e.target.value as AdminPrefeituraInput["status"] })}
                  options={[
                    { value: "ativo", label: "Ativo" },
                    { value: "pausado", label: "Pausado" },
                    // Cadastro vindo do site institucional nasce pendente. A opcao
                    // existe pra o operador VER a situacao atual (select sem o valor
                    // corrente aparece vazio) e pra poder salvar outros campos sem
                    // aprovar pela esteira. Aprovar = trocar pra Ativo.
                    { value: "pendente", label: "Pendente (aguardando aprovação)" },
                  ]}
                />
              )}
              <TextField
                label="E-mail de contato do responsável"
                {...campoEditavel(!visualizando, form.contatoEmail ?? "", (v) => setForm({ ...form, contatoEmail: v }))}
                type="email"
                placeholder="responsavel@orgao.gov.br"
              />
              <TextField
                label="URL do CSV da folha (modo CSV)"
                {...campoEditavel(!visualizando, form.folhaSincUrl ?? "", (v) => setForm({ ...form, folhaSincUrl: v }))}
                placeholder="https://orgao.gov.br/folha.csv"
              />
            </FormGrid>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "var(--text-muted)", cursor: visualizando ? "default" : "pointer" }}>
              <input
                type="checkbox"
                checked={form.distribuicaoAutomaticaDescontos ?? false}
                disabled={visualizando}
                onChange={(e) => setForm({ ...form, distribuicaoAutomaticaDescontos: e.target.checked })}
                style={{ marginTop: 3 }}
              />
              <span>
                <b style={{ color: "var(--text)" }}>Distribuir automaticamente os descontos do retorno</b>
                <br />
                Quando ligado, valores compatíveis são destinados aos contratos elegíveis durante o processamento.
                Pagamentos parciais priorizam o contrato com a parcela mais avançada. Divergências de cadastro continuam pendentes.
                Quando desligado, a distribuição deve ser confirmada em Folhas → Distribuição.
              </span>
            </label>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "var(--text-muted)", cursor: visualizando ? "default" : "pointer" }}>
              <input
                type="checkbox"
                checked={form.permiteServidorEditarContato ?? false}
                disabled={visualizando}
                onChange={(e) => setForm({ ...form, permiteServidorEditarContato: e.target.checked })}
                style={{ marginTop: 3 }}
              />
              <span>
                <b style={{ color: "var(--text)" }}>Permitir que servidores editem contato pelo app</b>
                <br />
                Se ligado, o servidor pode alterar e-mail e telefone em Meus dados. Se desligado, mostra somente-leitura.
              </span>
            </label>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "var(--text-muted)", cursor: visualizando ? "default" : "pointer" }}>
              <input
                type="checkbox"
                checked={form.exigeSenhaAverbacao === true}
                disabled={visualizando}
                onChange={(e) => setForm({ ...form, exigeSenhaAverbacao: e.target.checked })}
                style={{ marginTop: 3 }}
              />
              <span>
                <b style={{ color: "var(--text)" }}>Exigir senha do servidor na averbação iniciada pela consignatária</b>
                <br />
                Quando a consignatária averba direto pela ficha Margem/Contratação (sem passar por proposta do servidor), ela
                precisa digitar a senha do servidor no momento da operação — o backend valida antes de averbar. Assim
                a consignatária só averba se o servidor de fato autorizou (só ele sabe a senha). Se desligar, a averbação é
                imediata — use com cuidado: a consignatária averba à revelia do servidor.
              </span>
            </label>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
                Exclusividades do Cartão Consignado (opcional)
              </label>
              <textarea
                value={form.exclusividadesCartaoConsig ?? ""}
                onChange={(e) => setForm({ ...form, exclusividadesCartaoConsig: e.target.value })}
                placeholder="Ex.: Cartão Elo Consignado com 1,5% a.m. exclusivo."
                maxLength={500}
                rows={3}
                readOnly={visualizando}
                style={{
                  width: "100%", padding: 10, borderRadius: 8,
                  border: "1px solid var(--border-strong)",
                  background: "var(--bg-elev)", color: "var(--text)",
                  fontSize: 13, resize: "vertical", fontFamily: "inherit",
                  boxSizing: "border-box",
                  ...(visualizando ? readOnlyInputStyle : {}),
                }}
              />
            </div>
          </div>
        </details>

        {error ? <div style={{ color: "var(--danger-500)", fontSize: 13 }}>{error}</div> : null}
        {(() => {
          // Aviso so aparece APOS o user clicar Salvar sem preencher tudo.
          if (!triedSubmit) return null;
          const faltando: string[] = [];
          if (!form.nome) faltando.push("razão social (busque o CNPJ)");
          if (ibgeInvalido) {
            faltando.push(municipioExigido && !ibgePreenchido
              ? "município (obrigatório na esfera municipal — digite o código IBGE)"
              : "código IBGE com 7 dígitos");
          }
          if (!form.telefone) faltando.push("telefone");
          if (exigeCredencial && !form.loginEmail) faltando.push("e-mail de login");
          if (faltando.length === 0) return null;
          return (
            <div style={{ fontSize: 12, color: "var(--gold-500)", padding: "8px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--gold-500) 30%, transparent)" }}>
              ⚠ Falta preencher: {faltando.join(", ")}.
            </div>
          );
        })()}
        {/* Visualizar nao submete nada — so fecha. */}
        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose}>{visualizando ? "Fechar" : "Cancelar"}</Button>
          {visualizando ? null : (
          <Button
            type="button"
            variant={aprovando ? "success" : "primary"}
            disabled={save.isPending}
            onClick={() => {
              // Validacao: se algo faltar, marca triedSubmit e nao dispara save.
              // Vale igual no modo aprovar — cadastro do site pode chegar sem
              // codigo IBGE (grava 0), e o aviso instrui a refazer a busca CNPJ.
              const faltando =
                !form.nome
                || (exigeCredencial && !form.loginEmail)
                || !form.telefone
                || ibgeInvalido;
              if (faltando) {
                setTriedSubmit(true);
                return;
              }
              save.mutate();
            }}
          >
            {aprovando
              ? (save.isPending ? "Aprovando..." : "Confirmar e aprovar cadastro")
              : (save.isPending ? "Salvando..." : "Salvar")}
          </Button>
          )}
        </FormActions>
      </div>

      {/* So alcancavel pelo modo aprovar — ver o botao no bloco de dados oficiais. */}
      {confirmandoEdicao ? (
        <ConfirmarEdicaoManualModal
          onConfirm={() => { setEdicaoManual(true); setConfirmandoEdicao(false); }}
          onClose={() => setConfirmandoEdicao(false)}
        />
      ) : null}
    </div>
  );
}

// Backdrop cobre a viewport toda. overflow-y no proprio backdrop garante que
// modal maior que a tela role NELE (nao no body de tras). padding vertical
// evita que o modal cole nas bordas quando ocupa a altura maxima.
const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(10,22,40,.6)",
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "24px 16px", overflowY: "auto",
  zIndex: 100, backdropFilter: "blur(6px)",
};
// maxHeight impede o card de exceder a viewport; overflowY:auto faz o proprio
// card rolar quando o conteudo passa (fluxo CNPJ + dados + credenciais +
// opcoes avancadas fica alto num modal so).
const modalCard: React.CSSProperties = {
  background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
  borderRadius: 14, padding: 24, maxWidth: 640, width: "calc(100% - 32px)",
  maxHeight: "calc(100vh - 48px)", overflowY: "auto",
  display: "flex", flexDirection: "column", gap: 16, boxShadow: "var(--shadow-lg)",
  margin: "auto",
};

// LimparBaseModal removida 27/07/2026 — codigo morto desde que o botao
// trigger foi retirado. Reintroduzir junto com o botao se necessario.
